import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge, SeverityBadge, fmtDate, fmtDKK } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AiInsightsCard } from "@/components/AiInsightsCard";
import {
  UserRound, Dog, Users, CalendarDays, CalendarRange, Percent, ClipboardList,
  CheckCircle2, UserPlus, UserX, TrendingUp, Gauge, Plus, GraduationCap,
  ClipboardCheck, Phone, AlertTriangle, Clock, PawPrint,
} from "lucide-react";
import { toast } from "sonner";

const KPIS = [
  { k: "active_clients", label: "Active Clients", icon: UserRound },
  { k: "active_dogs", label: "Active Dogs", icon: Dog },
  { k: "active_trainers", label: "Active Trainers", icon: Users },
  { k: "upcoming_classes", label: "Upcoming Classes", icon: CalendarDays },
  { k: "classes_this_week", label: "Classes This Week", icon: CalendarRange },
  { k: "attendance_rate", label: "Attendance Rate", icon: Percent, suffix: "%" },
  { k: "homework_assigned", label: "Homework Assigned", icon: ClipboardList },
  { k: "homework_completed", label: "Homework Completed", icon: CheckCircle2 },
  { k: "new_clients_month", label: "New Clients (month)", icon: UserPlus },
  { k: "dogs_without_trainer", label: "Dogs w/o Trainer", icon: UserX },
  { k: "capacity_usage", label: "Capacity Usage", icon: Gauge, suffix: "%" },
];

export default function LocalDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/local/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const k = data?.kpis || {};
  const QUICK = [
    { label: "Add Client", icon: UserPlus, onClick: () => navigate("/clients"), testid: "lqa-add-client" },
    { label: "Add Dog", icon: Dog, onClick: () => navigate("/dogs"), testid: "lqa-add-dog" },
    { label: "Add Class", icon: Plus, onClick: () => navigate("/classes"), testid: "lqa-add-class" },
    { label: "Assign Trainer", icon: Users, onClick: () => navigate("/trainers"), testid: "lqa-assign-trainer" },
    { label: "Assign Homework", icon: ClipboardCheck, onClick: () => navigate("/homework"), testid: "lqa-assign-homework" },
    { label: "Today's Classes", icon: CalendarDays, onClick: () => navigate("/classes"), testid: "lqa-today" },
    { label: "Contact Client", icon: Phone, onClick: () => toast.info("Messaging coming with HV Customer App"), testid: "lqa-contact" },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6" data-testid="local-quick-actions">
        {QUICK.map((a) => (
          <Button key={a.label} variant="outline" size="sm" className="gap-2" onClick={a.onClick} data-testid={a.testid}>
            <a.icon size={15} strokeWidth={1.5} /> {a.label}
          </Button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4">
        {KPIS.map((kpi) => (
          <Card key={kpi.k} className="border-border" data-testid={`local-kpi-${kpi.k}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground mb-2">
                <kpi.icon size={15} strokeWidth={1.5} />
                <span className="text-[11px] uppercase tracking-wide font-semibold">{kpi.label}</span>
              </div>
              <div className="text-2xl font-heading font-semibold tracking-tight">
                {data ? k[kpi.k] : "—"}{kpi.suffix && data ? kpi.suffix : ""}
              </div>
            </CardContent>
          </Card>
        ))}
        <Card className="border-border bg-primary text-primary-foreground" data-testid="local-kpi-revenue_placeholder">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-primary-foreground/70 mb-2"><TrendingUp size={15} strokeWidth={1.5} /><span className="text-[11px] uppercase tracking-wide font-semibold">Revenue</span></div>
            <div className="text-2xl font-heading font-semibold tracking-tight">{data ? fmtDKK(k.revenue_placeholder) : "—"}</div>
            <div className="text-[10px] text-primary-foreground/60 mt-1">Placeholder · HV Payments</div>
          </CardContent>
        </Card>
      </div>

      <div className="mb-6"><AiInsightsCard /></div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Today / This week */}
        <Card className="border-border lg:col-span-2">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Clock size={18} strokeWidth={1.5} /> Today &amp; This Week</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase tracking-wide font-semibold text-muted-foreground">Classes Today</span>
                <span className="text-xs text-muted-foreground">{data?.today?.dogs_today ?? 0} dogs attending</span>
              </div>
              {data?.today?.classes_today?.length ? data.today.classes_today.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-3 rounded-lg border border-border/60 mb-2" data-testid="local-today-class">
                  <div><div className="font-medium text-sm">{c.name}</div><div className="text-xs text-muted-foreground">{c.trainer_name} · {c.time}</div></div>
                  <StatusBadge value={c.status} />
                </div>
              )) : <p className="text-sm text-muted-foreground">No classes scheduled today.</p>}
              {data?.today?.trainers_today?.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {data.today.trainers_today.map((t) => <span key={t} className="text-xs px-2 py-1 rounded-full bg-secondary text-secondary-foreground">{t}</span>)}
                </div>
              )}
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2">Classes This Week</div>
              {data?.week?.classes_this_week?.length ? data.week.classes_this_week.map((c) => (
                <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg border border-border/60 mb-1.5" data-testid="local-week-class">
                  <span className="text-sm">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate(c.date)} · {c.time}</span>
                </div>
              )) : <p className="text-sm text-muted-foreground">No classes this week.</p>}
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <MiniList title="Homework follow-up" icon={ClipboardList} items={data?.today?.homework_followup} render={(h) => `${h.title} · ${h.dog_name}`} empty="All homework on track." testid="local-hw-followup" />
              <MiniList title="Clients needing contact" icon={Phone} items={data?.today?.clients_need_contact} render={(c) => c.full_name} empty="Everyone engaged." testid="local-need-contact" />
              <MiniList title="New dog registrations" icon={PawPrint} items={data?.today?.new_dogs} render={(d) => `${d.name} · ${d.breed}`} empty="No new dogs this month." testid="local-new-dogs" />
            </div>
          </CardContent>
        </Card>

        {/* Local attention */}
        <Card className="border-border">
          <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><AlertTriangle size={18} strokeWidth={1.5} className="text-amber-600" /> Attention Center</CardTitle></CardHeader>
          <CardContent className="space-y-2 max-h-[520px] overflow-y-auto">
            {data?.attention?.length ? data.attention.map((a) => (
              <div key={`${a.category}-${a.title}`} className={`p-3 rounded-lg border ${a.severity === "critical" ? "border-red-200 bg-red-50" : a.severity === "warning" ? "border-amber-200 bg-amber-50" : "border-border bg-muted/40"}`} data-testid="local-attention-item">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">{a.category}</span>
                  <SeverityBadge value={a.severity} />
                </div>
                <div className="text-sm font-medium mt-1">{a.title}</div>
                {a.detail && <div className="text-xs text-muted-foreground mt-0.5">{a.detail}</div>}
              </div>
            )) : <p className="text-sm text-muted-foreground py-6 text-center">Location running smoothly ✅</p>}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function MiniList({ title, icon: Icon, items, render, empty, testid }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide font-semibold text-muted-foreground mb-2 flex items-center gap-1.5"><Icon size={13} /> {title}</div>
      <div className="space-y-1.5" data-testid={testid}>
        {items?.length ? items.map((it, i) => (
          <div key={i} className="text-sm p-2 rounded-md bg-muted/40">{render(it)}</div>
        )) : <p className="text-sm text-muted-foreground">{empty}</p>}
      </div>
    </div>
  );
}
